import { useEffect } from 'react';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';

export interface LayoutState {
  /** Whether the asset group editor tab is visible. */
  assetGroupEditorVisible: boolean;
  /** Whether the table definition (scaling table) editor tab is visible. */
  tableDefinitionEditorVisible: boolean;
  /** Whether the stored table editor tab is visible. */
  storedTableEditorVisible: boolean;
  /** Whether a display pane add-on plugin has enabled the capsules panel. */
  displayPaneAddOnCapsulesPanelEnabled: boolean;
  /** Whether a display pane add-on plugin has enabled the details panel. */
  displayPaneAddOnDetailsPanelEnabled: boolean;
  /** Whether the trend tab is visible. */
  trendVisible: boolean;
  /** Pulse flag set to bring the tools tab into focus. */
  autoFocusToolsTab: boolean;
  /** Pulse flag set to bring the data tab into focus. */
  autoFocusDataTab: boolean;
  /** Pulse flag set to trigger the worksheet layout resetting back to its default. */
  reset: boolean;
  /** Pulse flag set to trigger a resize of the journal toolbar. */
  resetJournalToolbar: boolean;
  /** Whether the journal tab is visible. */
  journalTabVisible: boolean;
  /** Whether the formula editor is displayed as a full tab instead of a floating popup. */
  formulaInTab: boolean;
  /** Anchor position used to position the floating formula help popup. */
  formulaTabAnchorPosition: { top: number; left: number; height: number; width: number };
  /** Always `null`. Unused legacy field retained only for persisted-state schema compatibility. */
  layout: null;
  /** Pulse flag set to bring the trend tab into focus. */
  autoFocusTrendTab: boolean;
  /** Pulse flag set to bring the table definition tab into focus. */
  autoFocusTableDefinitionTab: boolean;
  /** Pulse flag set to bring the stored table tab into focus. */
  autoFocusStoredTableTab: boolean;
  /** Whether the statistics summary tab is visible. */
  statisticsSummaryTabVisible: boolean;
}

export type LayoutDehydratedState = Omit<
  LayoutState,
  'reset' | 'resetJournalToolbar' | 'journalTabVisible' | 'autoFocusToolsTab' | 'autoFocusDataTab'
>;

export interface LayoutActions {
  setAssetGroupEditorVisible: (visible: boolean) => void;
  setTableDefinitionEditorVisible: (visible: boolean) => void;
  setStoredTableEditorVisible: (visible: boolean) => void;
  setDisplayPaneAddOnCapsulePanelEnabled: (enabled: boolean) => void;
  setDisplayPaneAddOnDetailsPanelEnabled: (enabled: boolean) => void;
  setTrendVisible: (visible: boolean) => void;
  setAutoFocusToolsTab: (autofocus: boolean) => void;
  setAutoFocusDataTab: (autofocus: boolean) => void;
  resetLayout: (reset: boolean) => void;
  resetJournalToolbar: (reset: boolean) => void;
  setJournalTabVisible: (visible: boolean) => void;
  setFormulaInTab: (inTab: boolean) => void;
  setFormulaTabAnchorPosition: (position: { top: number; left: number; height: number; width: number }) => void;
  setAutoFocusTrendTab: (autofocus: boolean) => void;
  setAutoFocusTableDefinitionTab: (autofocus: boolean) => void;
  setAutoFocusStoredTableTab: (autofocus: boolean) => void;
  setStatisticsSummaryTabVisible: (visible: boolean) => void;
  /** Restores the entire store to its initial state. Used by the persistence bridge's force-rehydrate path. */
  reset: () => void;
}

const initialState: LayoutState = Object.freeze({
  assetGroupEditorVisible: false,
  tableDefinitionEditorVisible: false,
  storedTableEditorVisible: false,
  displayPaneAddOnCapsulesPanelEnabled: false,
  displayPaneAddOnDetailsPanelEnabled: false,
  trendVisible: true,
  autoFocusToolsTab: false,
  autoFocusDataTab: false,
  reset: false,
  resetJournalToolbar: false,
  journalTabVisible: false,
  formulaInTab: false,
  formulaTabAnchorPosition: { top: 0, left: 0, height: 0, width: 0 },
  layout: null,
  autoFocusTrendTab: false,
  autoFocusTableDefinitionTab: false,
  autoFocusStoredTableTab: false,
  statisticsSummaryTabVisible: false,
});

export const dehydrateLayoutState = (state: LayoutState): LayoutDehydratedState => {
  const { reset, resetJournalToolbar, journalTabVisible, autoFocusToolsTab, autoFocusDataTab, ...dehydrated } = state;
  return dehydrated;
};

const layoutActionsFactory: ActionsFactory<LayoutState, LayoutActions> = (set, _get, resetState) => ({
  setAssetGroupEditorVisible: (visible) =>
    set((draft) => {
      draft.assetGroupEditorVisible = visible;
    }),

  setTableDefinitionEditorVisible: (visible) =>
    set((draft) => {
      draft.tableDefinitionEditorVisible = visible;
    }),

  setStoredTableEditorVisible: (visible) =>
    set((draft) => {
      draft.storedTableEditorVisible = visible;
    }),

  setDisplayPaneAddOnCapsulePanelEnabled: (enabled) =>
    set((draft) => {
      draft.displayPaneAddOnCapsulesPanelEnabled = enabled;
    }),

  setDisplayPaneAddOnDetailsPanelEnabled: (enabled) =>
    set((draft) => {
      draft.displayPaneAddOnDetailsPanelEnabled = enabled;
    }),

  setTrendVisible: (visible) =>
    set((draft) => {
      draft.trendVisible = visible;
    }),

  setAutoFocusToolsTab: (autofocus) =>
    set((draft) => {
      draft.autoFocusToolsTab = autofocus;
    }),

  setAutoFocusDataTab: (autofocus) =>
    set((draft) => {
      draft.autoFocusDataTab = autofocus;
    }),

  resetLayout: (reset) =>
    set((draft) => {
      draft.reset = reset;
    }),

  resetJournalToolbar: (reset) =>
    set((draft) => {
      draft.resetJournalToolbar = reset;
    }),

  setJournalTabVisible: (visible) =>
    set((draft) => {
      draft.journalTabVisible = visible;
    }),

  setFormulaInTab: (inTab) =>
    set((draft) => {
      draft.formulaInTab = inTab;
    }),

  setFormulaTabAnchorPosition: (position) =>
    set((draft) => {
      draft.formulaTabAnchorPosition = position;
    }),

  setAutoFocusTrendTab: (autofocus) =>
    set((draft) => {
      draft.autoFocusTrendTab = autofocus;
    }),

  setAutoFocusTableDefinitionTab: (autofocus) =>
    set((draft) => {
      draft.autoFocusTableDefinitionTab = autofocus;
    }),

  setAutoFocusStoredTableTab: (autofocus) =>
    set((draft) => {
      draft.autoFocusStoredTableTab = autofocus;
    }),

  setStatisticsSummaryTabVisible: (visible) =>
    set((draft) => {
      draft.statisticsSummaryTabVisible = visible;
    }),

  reset: resetState,
});

const STORE_NAME = 'Layout';

const [LayoutStoreProvider, useLayoutStoreInstance, useLayoutSelector] = createStore(
  STORE_NAME,
  initialState,
  layoutActionsFactory,
);

export { LayoutStoreProvider };

export const useLayoutAssetGroupEditorVisible = () => useLayoutSelector((state) => state.assetGroupEditorVisible);
export const useLayoutTableDefinitionEditorVisible = () =>
  useLayoutSelector((state) => state.tableDefinitionEditorVisible);
export const useLayoutStoredTableEditorVisible = () => useLayoutSelector((state) => state.storedTableEditorVisible);
export const useLayoutDisplayPaneAddOnCapsulesPanelEnabled = () =>
  useLayoutSelector((state) => state.displayPaneAddOnCapsulesPanelEnabled);
export const useLayoutDisplayPaneAddOnDetailsPanelEnabled = () =>
  useLayoutSelector((state) => state.displayPaneAddOnDetailsPanelEnabled);
export const useLayoutTrendVisible = () => useLayoutSelector((state) => state.trendVisible);
export const useLayoutAutoFocusToolsTab = () => useLayoutSelector((state) => state.autoFocusToolsTab);
export const useLayoutAutoFocusDataTab = () => useLayoutSelector((state) => state.autoFocusDataTab);
export const useLayoutReset = () => useLayoutSelector((state) => state.reset);
export const useLayoutResetJournalToolbar = () => useLayoutSelector((state) => state.resetJournalToolbar);
export const useLayoutJournalTabVisible = () => useLayoutSelector((state) => state.journalTabVisible);
export const useLayoutFormulaInTab = () => useLayoutSelector((state) => state.formulaInTab);
export const useLayoutFormulaTabAnchorPosition = () => useLayoutSelector((state) => state.formulaTabAnchorPosition);
export const useLayoutAutoFocusTrendTab = () => useLayoutSelector((state) => state.autoFocusTrendTab);
export const useLayoutAutoFocusTableDefinitionTab = () =>
  useLayoutSelector((state) => state.autoFocusTableDefinitionTab);
export const useLayoutAutoFocusStoredTableTab = () => useLayoutSelector((state) => state.autoFocusStoredTableTab);
export const useLayoutStatisticsSummaryTabVisible = () =>
  useLayoutSelector((state) => state.statisticsSummaryTabVisible);

export const useLayoutActions = () => useLayoutStoreInstance().actions;

export function createTestLayoutStore(overrides: Partial<LayoutState> = {}) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestLayoutStore() should only be used in tests.');
  }

  return createStoreInstance({ ...initialState, ...overrides }, layoutActionsFactory);
}

/**
 * Legacy function to create a singleton Layout store instance.
 *
 * @deprecated This is only for backward compatibility with non-React code (e.g., action files, utilities).
 * New code should use the LayoutStoreProvider and hooks instead.
 */
export function legacyCreateLayoutStore() {
  return createStoreInstance(initialState, layoutActionsFactory, { storeName: STORE_NAME });
}

export const usePersistLayoutStore = () => {
  const register = useRegisterPersistedStore();
  const store = useLayoutStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqLayoutStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateLayoutState,
    });
  }, [register, store]);
};
